import React, { useState, useMemo, useEffect } from 'react';
import { Row, Col, Button, ButtonGroup, Modal } from 'react-bootstrap';

export interface DateRange {
  start_date: string;
  end_date: string;
}

export type NavigationMode = 'month' | 'quarter' | 'year' | 'custom' | 'all';

// 'all' mode emits empty strings for start_date / end_date. Consumers
// already do `if (dateRange.start_date) params.set(...)`, so omitting
// the params from the API URL falls out naturally — backend treats
// missing dates as "no filter".
const ALL_MODE_START_DATE = '';
const ALL_MODE_END_DATE = '';
export type DateFormat = 'date-only' | 'iso8601';

export interface PeriodChangeEvent {
  navigationMode: NavigationMode;
  dateRange: DateRange;
  period: string; // formatted display string like "2025-09", "2025-Q3", "2025"
}

interface DatePeriodNavigationProps {
  initialDateRange?: DateRange;
  initialNavigationMode?: NavigationMode;
  dateFormat?: DateFormat; // 'date-only' (default): YYYY-MM-DD, 'iso8601': full timestamp with timezone
  onPeriodChange: (event: PeriodChangeEvent) => void;
}

const DatePeriodNavigation: React.FC<DatePeriodNavigationProps> = ({
  initialDateRange,
  initialNavigationMode = 'month',
  dateFormat = 'date-only',
  onPeriodChange,
}) => {
  // Initialize with current month if no initial range provided
  const getInitialDateRange = (): DateRange => {
    if (initialDateRange) return initialDateRange;
    
    const now = new Date();
    const year = now.getFullYear();
    const month = now.getMonth() + 1;
    const startDate = `${year}-${String(month).padStart(2, '0')}-01`;
    const daysInMonth = new Date(year, month, 0).getDate();
    const endDate = `${year}-${String(month).padStart(2, '0')}-${String(daysInMonth).padStart(2, '0')}`;
    
    return {
      start_date: startDate,
      end_date: endDate,
    };
  };

  // Internal state management
  const [navigationMode, setNavigationMode] = useState<NavigationMode>(initialNavigationMode);
  const [dateRange, setDateRange] = useState<DateRange>(getInitialDateRange);
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [calendarDate, setCalendarDate] = useState(new Date());
  const [selectedMonth, setSelectedMonth] = useState<number | null>(null);
  const [selectedYear, setSelectedYear] = useState<number | null>(null);
  const [selectedQuarter, setSelectedQuarter] = useState<number | null>(null);
  const [customStartDate, setCustomStartDate] = useState<string>('');
  const [customEndDate, setCustomEndDate] = useState<string>('');

  // Calculate current period display
  const currentPeriod = useMemo(() => {
    if (navigationMode === 'all') {
      return 'All time';
    }
    if (navigationMode === 'custom') {
      return `${dateRange.start_date} to ${dateRange.end_date}`;
    }

    const [startYear, startMonth] = dateRange.start_date.split('-').map(Number);

    if (navigationMode === 'quarter') {
      const quarter = Math.floor((startMonth - 1) / 3) + 1;
      return `${startYear}-Q${quarter}`;
    } else if (navigationMode === 'year') {
      return `${startYear}`;
    } else {
      // Single month format
      return `${startYear}-${String(startMonth).padStart(2, '0')}`;
    }
  }, [dateRange, navigationMode]);

  // Check if we can navigate to the next period (always allow)
  const canNavigateNext = useMemo(() => {
    return true; // Allow navigation to future periods
  }, []);

  // Format a YYYY-MM-DD date range for output based on dateFormat config
  const formatOutputRange = (range: DateRange): DateRange => {
    if (dateFormat === 'date-only') return range;

    // iso8601: convert start to beginning of day, end to end of day in local timezone
    const tzOffset = new Date().getTimezoneOffset();
    const tzHours = Math.abs(Math.floor(tzOffset / 60));
    const tzMinutes = Math.abs(tzOffset % 60);
    const tzSign = tzOffset <= 0 ? '+' : '-';
    const tz = `${tzSign}${String(tzHours).padStart(2, '0')}:${String(tzMinutes).padStart(2, '0')}`;

    return {
      start_date: `${range.start_date}T00:00:00${tz}`,
      end_date: `${range.end_date}T23:59:59${tz}`,
    };
  };

  // Emit change event
  const emitPeriodChange = (newNavigationMode: NavigationMode, newDateRange: DateRange) => {
    let period: string;

    if (newNavigationMode === 'all') {
      period = 'all';
    } else if (newNavigationMode === 'custom') {
      period = `${newDateRange.start_date} to ${newDateRange.end_date}`;
    } else {
      const [startYear, startMonth] = newDateRange.start_date.split('-').map(Number);

      if (newNavigationMode === 'quarter') {
        const quarter = Math.floor((startMonth - 1) / 3) + 1;
        period = `${startYear}-Q${quarter}`;
      } else if (newNavigationMode === 'year') {
        period = `${startYear}`;
      } else {
        period = `${startYear}-${String(startMonth).padStart(2, '0')}`;
      }
    }

    onPeriodChange({
      navigationMode: newNavigationMode,
      dateRange: formatOutputRange(newDateRange),
      period,
    });
  };

  // Internal update function that updates state and emits event
  const updatePeriod = (newNavigationMode: NavigationMode, newDateRange: DateRange) => {
    setNavigationMode(newNavigationMode);
    setDateRange(newDateRange);
    emitPeriodChange(newNavigationMode, newDateRange);
  };

  // anchorYearMonth picks the year/month to seed a mode switch from.
  // When leaving 'all' mode, the current dateRange holds the sentinel
  // 0001-01-01 / 9999-12-31 — parsing those would land us in year 1
  // (and then template-literal interpolation would emit a non-zero-padded
  // "1-01-01" that the OpenAPI date validator rejects). Reset to today
  // for that case.
  const anchorYearMonth = (): { year: number; month: number } => {
    if (navigationMode === 'all') {
      const now = new Date();
      return { year: now.getFullYear(), month: now.getMonth() + 1 };
    }
    const [y, m] = dateRange.start_date.split('-').map(Number);
    return { year: y, month: m };
  };

  // Navigation mode functions
  const switchToMonthMode = () => {
    const { year, month } = anchorYearMonth();
    const newStartDate = `${year}-${String(month).padStart(2, '0')}-01`;
    const daysInMonth = new Date(year, month, 0).getDate();
    const newEndDate = `${year}-${String(month).padStart(2, '0')}-${String(daysInMonth).padStart(2, '0')}`;

    updatePeriod('month', { start_date: newStartDate, end_date: newEndDate });
  };

  const switchToQuarterMode = () => {
    const { year, month } = anchorYearMonth();
    const quarter = Math.floor((month - 1) / 3) + 1;
    const quarterStartMonth = (quarter - 1) * 3 + 1;
    const quarterEndMonth = quarterStartMonth + 2;

    const newStartDate = `${year}-${String(quarterStartMonth).padStart(2, '0')}-01`;
    const daysInEndMonth = new Date(year, quarterEndMonth, 0).getDate();
    const newEndDate = `${year}-${String(quarterEndMonth).padStart(2, '0')}-${String(daysInEndMonth).padStart(2, '0')}`;

    updatePeriod('quarter', { start_date: newStartDate, end_date: newEndDate });
  };

  const switchToYearMode = () => {
    const { year } = anchorYearMonth();
    updatePeriod('year', { start_date: `${year}-01-01`, end_date: `${year}-12-31` });
  };

  const switchToCustomMode = () => {
    // Keep current date range when switching to custom mode
    // User will select custom dates via the date picker.
    // Exception: when leaving 'all', the sentinel 0001/9999 range would
    // make prev/next navigation produce huge daysDiff jumps — reset to
    // today's month so the picker opens somewhere sane.
    if (navigationMode === 'all') {
      const { year, month } = anchorYearMonth();
      const newStartDate = `${year}-${String(month).padStart(2, '0')}-01`;
      const daysInMonth = new Date(year, month, 0).getDate();
      const newEndDate = `${year}-${String(month).padStart(2, '0')}-${String(daysInMonth).padStart(2, '0')}`;
      updatePeriod('custom', { start_date: newStartDate, end_date: newEndDate });
      return;
    }
    updatePeriod('custom', dateRange);
  };

  const switchToAllMode = () => {
    updatePeriod('all', { start_date: ALL_MODE_START_DATE, end_date: ALL_MODE_END_DATE });
  };

  const handlePreviousPeriod = () => {
    let newStartDate, newEndDate;

    if (navigationMode === 'custom') {
      // Calculate the number of days in the current custom range
      const startDate = new Date(dateRange.start_date);
      const endDate = new Date(dateRange.end_date);
      const daysDiff = Math.round((endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24));

      // Move backward by that number of days
      const newStart = new Date(startDate);
      newStart.setDate(newStart.getDate() - daysDiff - 1); // -1 to make ranges non-overlapping

      const newEnd = new Date(startDate);
      newEnd.setDate(newEnd.getDate() - 1); // Day before current start

      newStartDate = newStart.toISOString().split('T')[0];
      newEndDate = newEnd.toISOString().split('T')[0];
    } else {
      const [startYear, startMonth] = dateRange.start_date.split('-').map(Number);

      if (navigationMode === 'quarter') {
        const currentQuarter = Math.floor((startMonth - 1) / 3) + 1;
        let prevQuarter = currentQuarter - 1;
        let prevYear = startYear;

        if (prevQuarter < 1) {
          prevQuarter = 4;
          prevYear = startYear - 1;
        }

        const prevQuarterStartMonth = (prevQuarter - 1) * 3 + 1;
        const prevQuarterEndMonth = prevQuarterStartMonth + 2;

        newStartDate = `${prevYear}-${String(prevQuarterStartMonth).padStart(2, '0')}-01`;
        const daysInEndMonth = new Date(prevYear, prevQuarterEndMonth, 0).getDate();
        newEndDate = `${prevYear}-${String(prevQuarterEndMonth).padStart(2, '0')}-${String(daysInEndMonth).padStart(2, '0')}`;
      } else if (navigationMode === 'year') {
        const prevYear = startYear - 1;
        newStartDate = `${prevYear}-01-01`;
        newEndDate = `${prevYear}-12-31`;
      } else {
        let prevMonth = startMonth - 1;
        let prevYear = startYear;

        if (prevMonth < 1) {
          prevMonth = 12;
          prevYear = startYear - 1;
        }

        newStartDate = `${prevYear}-${String(prevMonth).padStart(2, '0')}-01`;
        const daysInMonth = new Date(prevYear, prevMonth, 0).getDate();
        newEndDate = `${prevYear}-${String(prevMonth).padStart(2, '0')}-${String(daysInMonth).padStart(2, '0')}`;
      }
    }

    const newDateRange = {
      start_date: newStartDate,
      end_date: newEndDate
    };

    updatePeriod(navigationMode, newDateRange);
  };

  const handleNextPeriod = () => {
    let newStartDate, newEndDate;

    if (navigationMode === 'custom') {
      // Calculate the number of days in the current custom range
      const startDate = new Date(dateRange.start_date);
      const endDate = new Date(dateRange.end_date);
      const daysDiff = Math.round((endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24));

      // Move forward by that number of days
      const newStart = new Date(endDate);
      newStart.setDate(newStart.getDate() + 1); // Day after current end

      const newEnd = new Date(newStart);
      newEnd.setDate(newEnd.getDate() + daysDiff);

      newStartDate = newStart.toISOString().split('T')[0];
      newEndDate = newEnd.toISOString().split('T')[0];
    } else {
      const [startYear, startMonth] = dateRange.start_date.split('-').map(Number);

      if (navigationMode === 'quarter') {
        const currentQuarter = Math.floor((startMonth - 1) / 3) + 1;
        let nextQuarter = currentQuarter + 1;
        let nextYear = startYear;

        if (nextQuarter > 4) {
          nextQuarter = 1;
          nextYear = startYear + 1;
        }

        const nextQuarterStartMonth = (nextQuarter - 1) * 3 + 1;
        const nextQuarterEndMonth = nextQuarterStartMonth + 2;

        newStartDate = `${nextYear}-${String(nextQuarterStartMonth).padStart(2, '0')}-01`;
        const daysInEndMonth = new Date(nextYear, nextQuarterEndMonth, 0).getDate();
        newEndDate = `${nextYear}-${String(nextQuarterEndMonth).padStart(2, '0')}-${String(daysInEndMonth).padStart(2, '0')}`;
      } else if (navigationMode === 'year') {
        const nextYear = startYear + 1;

        newStartDate = `${nextYear}-01-01`;
        newEndDate = `${nextYear}-12-31`;
      } else {
        let nextMonth = startMonth + 1;
        let nextYear = startYear;

        if (nextMonth > 12) {
          nextMonth = 1;
          nextYear = startYear + 1;
        }

        newStartDate = `${nextYear}-${String(nextMonth).padStart(2, '0')}-01`;
        const daysInMonth = new Date(nextYear, nextMonth, 0).getDate();
        newEndDate = `${nextYear}-${String(nextMonth).padStart(2, '0')}-${String(daysInMonth).padStart(2, '0')}`;
      }
    }

    const newDateRange = {
      start_date: newStartDate,
      end_date: newEndDate
    };

    updatePeriod(navigationMode, newDateRange);
  };

  const handleDatePickerOpen = () => {
    if (navigationMode === 'custom') {
      setCustomStartDate(dateRange.start_date);
      setCustomEndDate(dateRange.end_date);
    } else {
      const [startYear, startMonth] = dateRange.start_date.split('-').map(Number);

      setCalendarDate(new Date(startYear, startMonth - 1, 1));
      setSelectedYear(startYear);
      setSelectedMonth(startMonth);

      if (navigationMode === 'quarter') {
        const quarter = Math.floor((startMonth - 1) / 3) + 1;
        setSelectedQuarter(quarter);
      }
    }

    setShowDatePicker(true);
  };

  const handleDatePickerSave = () => {
    let newStartDate: string;
    let newEndDate: string;

    if (navigationMode === 'custom') {
      if (!customStartDate || !customEndDate) {
        return; // Exit early if no valid selection
      }
      newStartDate = customStartDate;
      newEndDate = customEndDate;
    } else if (navigationMode === 'month' && selectedYear && selectedMonth) {
      newStartDate = `${selectedYear}-${String(selectedMonth).padStart(2, '0')}-01`;
      const daysInMonth = new Date(selectedYear, selectedMonth, 0).getDate();
      newEndDate = `${selectedYear}-${String(selectedMonth).padStart(2, '0')}-${String(daysInMonth).padStart(2, '0')}`;
    } else if (navigationMode === 'quarter' && selectedYear && selectedQuarter) {
      const quarterStartMonth = (selectedQuarter - 1) * 3 + 1;
      const quarterEndMonth = quarterStartMonth + 2;

      newStartDate = `${selectedYear}-${String(quarterStartMonth).padStart(2, '0')}-01`;
      const daysInEndMonth = new Date(selectedYear, quarterEndMonth, 0).getDate();
      newEndDate = `${selectedYear}-${String(quarterEndMonth).padStart(2, '0')}-${String(daysInEndMonth).padStart(2, '0')}`;
    } else if (navigationMode === 'year' && selectedYear) {
      newStartDate = `${selectedYear}-01-01`;
      newEndDate = `${selectedYear}-12-31`;
    } else {
      return; // Exit early if no valid selection
    }

    const newDateRange = {
      start_date: newStartDate,
      end_date: newEndDate
    };

    updatePeriod(navigationMode, newDateRange);
    setShowDatePicker(false);
  };

  const handleTodayClick = () => {
    const today = new Date();
    const currentYear = today.getFullYear();
    const currentMonth = today.getMonth() + 1;
    
    setCalendarDate(today);
    setSelectedYear(currentYear);
    setSelectedMonth(currentMonth);
    
    if (navigationMode === 'quarter') {
      const currentQuarter = Math.floor((currentMonth - 1) / 3) + 1;
      setSelectedQuarter(currentQuarter);
    }
  };

  const navigateCalendar = (direction: 'prev' | 'next') => {
    const newDate = new Date(calendarDate);
    if (navigationMode === 'quarter' || navigationMode === 'year') {
      // For quarter and year modes, navigate by year
      if (direction === 'prev') {
        newDate.setFullYear(newDate.getFullYear() - 1);
      } else {
        newDate.setFullYear(newDate.getFullYear() + 1);
      }
    } else {
      // For month mode, navigate by month
      if (direction === 'prev') {
        newDate.setMonth(newDate.getMonth() - 1);
      } else {
        newDate.setMonth(newDate.getMonth() + 1);
      }
      // Auto-select the month when navigating
      setSelectedMonth(newDate.getMonth() + 1);
      setSelectedYear(newDate.getFullYear());
    }
    setCalendarDate(newDate);
  };

  const renderQuarterSelector = () => {
    const year = calendarDate.getFullYear();

    return (
      <div className="d-flex flex-wrap gap-2 justify-content-center">
        {[1, 2, 3, 4].map(quarter => {
          const isSelected = selectedQuarter === quarter && selectedYear === year;

          return (
            <Button
              key={quarter}
              variant={isSelected ? 'primary' : 'outline-primary'}
              size="lg"
              onClick={() => {
                setSelectedQuarter(quarter);
                setSelectedYear(year);
              }}
              style={{ minWidth: '80px' }}
            >
              Q{quarter}
            </Button>
          );
        })}
      </div>
    );
  };

  const renderMonthSelector = () => {
    const year = calendarDate.getFullYear();
    const today = new Date();
    const currentYear = today.getFullYear();

    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
                       'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

    return (
      <div>
        {/* Year Selection */}
        <div className="d-flex justify-content-center align-items-center mb-4">
          <Button
            variant="outline-secondary"
            size="lg"
            onClick={() => {
              const newDate = new Date(year - 1, calendarDate.getMonth(), 1);
              setCalendarDate(newDate);
            }}
            disabled={year <= currentYear - 10}
          >
            <i className="bi bi-chevron-left"></i>
          </Button>
          <div className="mx-3 text-center" style={{ minWidth: '120px' }}>
            <h2 className="text-primary mb-0">{year}</h2>
          </div>
          <Button
            variant="outline-secondary"
            size="lg"
            onClick={() => {
              const newDate = new Date(year + 1, calendarDate.getMonth(), 1);
              setCalendarDate(newDate);
            }}
          >
            <i className="bi bi-chevron-right"></i>
          </Button>
        </div>

        {/* Month Grid */}
        <div className="row g-2">
          {monthNames.map((monthName, index) => {
            const monthNumber = index + 1;
            const isSelected = selectedMonth === monthNumber && selectedYear === year;

            return (
              <div key={monthNumber} className="col-3">
                <Button
                  variant={isSelected ? 'primary' : 'outline-primary'}
                  size="lg"
                  className="w-100"
                  onClick={() => {
                    setSelectedMonth(monthNumber);
                    setSelectedYear(year);
                  }}
                  style={{ minHeight: '50px' }}
                >
                  {monthName}
                </Button>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  const renderYearSelector = () => {
    const currentYear = new Date().getFullYear();
    const displayYear = selectedYear || calendarDate.getFullYear();

    return (
      <div className="text-center py-4">
        <div className="d-flex justify-content-center align-items-center">
          <Button
            variant="outline-secondary"
            size="lg"
            onClick={() => {
              const newYear = displayYear - 1;
              setSelectedYear(newYear);
              setCalendarDate(new Date(newYear, 0, 1));
            }}
            disabled={displayYear <= currentYear - 10}
          >
            <i className="bi bi-chevron-left"></i>
          </Button>
          <div className="mx-3 text-center" style={{ minWidth: '120px' }}>
            <h2 className="text-primary mb-0">{displayYear}</h2>
          </div>
          <Button
            variant="outline-secondary"
            size="lg"
            onClick={() => {
              const newYear = displayYear + 1;
              setSelectedYear(newYear);
              setCalendarDate(new Date(newYear, 0, 1));
            }}
          >
            <i className="bi bi-chevron-right"></i>
          </Button>
        </div>
        <p className="text-muted mt-3">Use the arrows to navigate between years</p>
      </div>
    );
  };

  const renderCustomDatePicker = () => {
    return (
      <div className="py-3">
        <div className="mb-3">
          <label htmlFor="custom-start-date" className="form-label">Start Date</label>
          <input
            id="custom-start-date"
            type="date"
            className="form-control"
            value={customStartDate}
            onChange={(e) => setCustomStartDate(e.target.value)}
          />
        </div>
        <div className="mb-3">
          <label htmlFor="custom-end-date" className="form-label">End Date</label>
          <input
            id="custom-end-date"
            type="date"
            className="form-control"
            value={customEndDate}
            onChange={(e) => setCustomEndDate(e.target.value)}
            min={customStartDate}
          />
        </div>
      </div>
    );
  };

  // Emit initial state on mount
  useEffect(() => {
    emitPeriodChange(navigationMode, dateRange);
  }, []); // Only run once on mount

  return (
    <>
      <Row className="">
        <Col>
          <div className="d-flex justify-content-start">
            <div className="d-flex align-items-center gap-3">
              <ButtonGroup size="sm">
                <Button
                  variant={navigationMode === 'month' ? 'primary' : 'outline-primary'}
                  onClick={switchToMonthMode}
                >
                  Month
                </Button>
                <Button
                  variant={navigationMode === 'quarter' ? 'primary' : 'outline-primary'}
                  onClick={switchToQuarterMode}
                >
                  Quarter
                </Button>
                <Button
                  variant={navigationMode === 'year' ? 'primary' : 'outline-primary'}
                  onClick={switchToYearMode}
                >
                  Year
                </Button>
                <Button
                  variant={navigationMode === 'custom' ? 'primary' : 'outline-primary'}
                  onClick={switchToCustomMode}
                >
                  Custom
                </Button>
                <Button
                  variant={navigationMode === 'all' ? 'primary' : 'outline-primary'}
                  onClick={switchToAllMode}
                >
                  All
                </Button>
              </ButtonGroup>
              <div className="d-flex align-items-center gap-2">
                <Button
                  variant="outline-primary"
                  size="sm"
                  onClick={handlePreviousPeriod}
                  disabled={navigationMode === 'all'}
                >
                  <i className="bi bi-chevron-left"></i>
                </Button>
                <Button
                  variant="primary"
                  size="sm"
                  onClick={handleDatePickerOpen}
                  title={navigationMode === 'all' ? 'All time' : 'Click to select date'}
                  style={{ minWidth: '140px' }}
                  disabled={navigationMode === 'all'}
                >
                  <i className="bi bi-calendar-month me-2"></i>
                  {currentPeriod}
                </Button>
                <Button
                  variant="outline-primary"
                  size="sm"
                  onClick={handleNextPeriod}
                  disabled={!canNavigateNext || navigationMode === 'all'}
                >
                  <i className="bi bi-chevron-right"></i>
                </Button>
              </div>
            </div>
          </div>
        </Col>
      </Row>

      {/* Date Picker Modal */}
      <Modal show={showDatePicker} onHide={() => setShowDatePicker(false)}>
        <Modal.Header closeButton>
          <Modal.Title>
            Select {navigationMode === 'month' ? 'Month' : navigationMode === 'quarter' ? 'Quarter' : navigationMode === 'year' ? 'Year' : 'Custom Range'}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {navigationMode !== 'custom' && (
            <div className="d-flex justify-content-center align-items-center mb-3">
              <Button variant="outline-secondary" size="lg" onClick={handleTodayClick} className="d-flex align-items-center">
                <i className="bi bi-calendar me-2"></i>
                Today
              </Button>
            </div>
          )}

          {navigationMode === 'quarter' && (
            <div className="d-flex justify-content-center align-items-center mb-3">
              <Button variant="outline-secondary" size="lg" onClick={() => navigateCalendar('prev')}>
                <i className="bi bi-chevron-left"></i>
              </Button>
              <div className="mx-3 text-center" style={{ minWidth: '120px' }}>
                <h2 className="text-primary mb-0">{calendarDate.getFullYear()}</h2>
              </div>
              <Button variant="outline-secondary" size="lg" onClick={() => navigateCalendar('next')}>
                <i className="bi bi-chevron-right"></i>
              </Button>
            </div>
          )}

          {navigationMode === 'month' && (
            <div>
              {renderMonthSelector()}
            </div>
          )}

          {navigationMode === 'quarter' && (
            <div>
              {renderQuarterSelector()}
            </div>
          )}

          {navigationMode === 'year' && (
            <div>
              {renderYearSelector()}
            </div>
          )}

          {navigationMode === 'custom' && (
            <div>
              {renderCustomDatePicker()}
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDatePicker(false)}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleDatePickerSave}
            disabled={
              (navigationMode === 'month' && (!selectedMonth || !selectedYear)) ||
              (navigationMode === 'quarter' && (!selectedQuarter || !selectedYear)) ||
              (navigationMode === 'year' && !selectedYear) ||
              (navigationMode === 'custom' && (!customStartDate || !customEndDate))
            }
          >
            Apply
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default DatePeriodNavigation;
